import React, { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch } from "../utils/api";
import { useFeedback } from "../hooks/useFeedback";
import Spinner from "../components/ui/Spinner";
import CollapsibleCard from "../components/ui/CollapsibleCard";

/* ── Helpers ───────────────────────────────────────────────────── */

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("fr-FR", {
        timeZone: "Europe/Paris",
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const fmtErr = (s) => (s != null ? `${s.toFixed(1)}s` : "—");

const errClass = (s) => {
  if (s == null) return "text-gray-400";
  if (s < 5)     return "text-emerald-600 font-semibold";
  if (s < 15)    return "text-amber-600 font-semibold";
  return "text-red-600 font-semibold";
};

const CHANNEL_LABELS = { static: "Statique", transition: "Transition", dynamic: "Dynamique", orientation: "Orientation" };

/* ── Section : Statut du modèle ────────────────────────────────── */

function ModelStatus({ onReportGenerated, refreshSignal }) {
  const [status, setStatus]         = useState(null);
  const [generating, setGenerating] = useState(false);
  const [feedback, showFeedback]    = useFeedback();

  const load = useCallback(() =>
    apiFetch("/api/ai/model-status").then(setStatus).catch(() => {}), []);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const data = await apiFetch("/api/ai/generate-training-report", { method: "POST" });
      showFeedback("success", "Rapport généré.");
      load();
      onReportGenerated?.(data.generated_at);
    } catch (e) {
      showFeedback("error", e.message);
    } finally {
      setGenerating(false);
    }
  };

  const isStale = status && (
    !status.report_exists ||
    (status.last_trained_at && status.report_generated_at &&
      new Date(status.report_generated_at) < new Date(status.last_trained_at))
  );

  return (
    <CollapsibleCard icon="📊" title="Statut du modèle" defaultOpen>
      <div className="p-4 flex flex-col gap-3">
        {!status ? (
          <div className="flex items-center gap-2 text-sm text-gray-400"><Spinner /> Chargement…</div>
        ) : (
          <>
            <dl className="text-sm flex flex-col gap-1.5">
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500 shrink-0">Dernier entraînement</dt>
                <dd className="font-medium text-right">{fmtDate(status.last_trained_at)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500 shrink-0">Rapport généré le</dt>
                <dd className="font-medium text-right">{fmtDate(status.report_generated_at)}</dd>
              </div>
            </dl>

            {status.metrics && (
              <>
                <div className="divider" />
                <p className="text-xs font-semibold text-gray-600">Erreur moyenne par marqueur</p>
                <div className="grid grid-cols-4 gap-2 text-center">
                  {["t1", "t2", "t3", "t4"].map(mk => (
                    <div key={mk} className="rounded-lg border border-gray-100 bg-gray-50 p-2">
                      <div className="text-[10px] text-gray-400 mb-0.5">{mk.toUpperCase()}</div>
                      <div className={`text-sm ${errClass(status.metrics.mean_error_per_marker?.[mk])}`}>
                        {fmtErr(status.metrics.mean_error_per_marker?.[mk])}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 text-center">
                  Moyenne globale : {fmtErr(status.metrics.global_mean_error)}
                  {" · "}Médiane : {fmtErr(status.metrics.global_median_error)}
                  {" · "}{status.metrics.n_samples} samples
                </p>
                {status.metrics.n_samples_inverted > 0 && (
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="rounded-lg border border-gray-100 bg-gray-50 p-2">
                      <div className="text-[10px] text-gray-400 mb-0.5">
                        Normaux ({status.metrics.n_samples_normal})
                      </div>
                      <div className={`text-sm ${errClass(status.metrics.global_mean_error_normal)}`}>
                        {fmtErr(status.metrics.global_mean_error_normal)}
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-100 bg-gray-50 p-2">
                      <div className="text-[10px] text-gray-400 mb-0.5">
                        Inversés ({status.metrics.n_samples_inverted})
                      </div>
                      <div className={`text-sm ${errClass(status.metrics.global_mean_error_inverted)}`}>
                        {fmtErr(status.metrics.global_mean_error_inverted)}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {feedback && <div className={`feedback-${feedback.type}`}>{feedback.msg}</div>}

            <div className="flex items-center gap-2 flex-wrap">
              {isStale && (
                <button onClick={generateReport} disabled={generating} className="btn btn-sm btn-primary self-start">
                  {generating ? <><Spinner /> Génération…</> : "Générer le rapport"}
                </button>
              )}
              {status.report_exists && (
                <a
                  href="/api/ai/training-report.html"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-sm btn-secondary self-start"
                >
                  Voir rapport HTML ↗
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </CollapsibleCard>
  );
}

/* ── Section : Lancer un entraînement ──────────────────────────── */

function TrainingLauncher({ onTrainingComplete }) {
  const [epochs, setEpochs]         = useState(30);
  const [kFolds, setKFolds]         = useState(5);
  const [trainingStatus, setStatus] = useState(null);
  const [logs, setLogs]             = useState([]);
  const [showLogs, setShowLogs]     = useState(false);
  const pollingRef                  = useRef(null);
  const logsEndRef                  = useRef(null);
  const wasRunningRef               = useRef(false);
  const [feedback, showFeedback]    = useFeedback();

  const fetchStatus = useCallback(async () => {
    const s = await apiFetch("/api/ai/training-status").catch(() => null);
    setStatus(s);
    return s;
  }, []);

  const fetchLogs = useCallback(async () => {
    const data = await apiFetch("/api/ai/training-log?lines=80").catch(() => null);
    if (data?.lines) setLogs(data.lines);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  }, []);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      const s = await fetchStatus();
      await fetchLogs();
      if (s?.status === "running") {
        wasRunningRef.current = true;
      } else {
        stopPolling();
        // "Rapport d'entraînement" (08/10/2026, demande Jordan) : ai/train_hybrid.py génère déjà
        // training_report.json tout seul en fin de run — il manquait juste le signal pour que
        // l'UI (ModelStatus + TrainingReport, plus lisible que les logs bruts ci-dessous) se
        // rafraîchisse automatiquement, sans clic manuel sur "Générer le rapport".
        if (wasRunningRef.current) {
          wasRunningRef.current = false;
          onTrainingComplete?.();
        }
      }
    }, 2000);
  }, [fetchStatus, fetchLogs, stopPolling, onTrainingComplete]);

  useEffect(() => {
    fetchStatus().then(s => {
      if (s?.status === "running") { fetchLogs(); startPolling(); }
    });
    return stopPolling;
  }, []);

  useEffect(() => {
    if (showLogs) logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs, showLogs]);

  const launch = async () => {
    try {
      const data = await apiFetch(
        `/api/ai/train?epochs=${epochs}&k_folds=${kFolds}`, { method: "POST" }
      );
      if (data.status === "already_running") {
        showFeedback("error", "Un entraînement est déjà en cours.");
        return;
      }
      setLogs([]);
      setShowLogs(true);
      fetchStatus();
      startPolling();
    } catch (e) {
      showFeedback("error", e.message);
    }
  };

  const isRunning = trainingStatus?.status === "running";
  const progress  = isRunning && trainingStatus.total_epochs > 0
    ? Math.round((trainingStatus.current_epoch / trainingStatus.total_epochs) * 100)
    : null;

  return (
    <CollapsibleCard icon="🚀" title="Entraînement" defaultOpen>
      <div className="p-4 flex flex-col gap-3">
        {isRunning ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span className="flex items-center gap-1.5"><Spinner /> En cours…</span>
              <span>{trainingStatus.current_epoch} / {trainingStatus.total_epochs} epochs</span>
            </div>
            {progress != null && (
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                  className="bg-violet-500 h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-gray-500 shrink-0">Epochs</label>
            <input
              type="number" min="1" max="1000" value={epochs}
              onChange={e => setEpochs(Math.max(1, Number(e.target.value)))}
              className="input w-20 text-center"
            />
            <label className="text-xs text-gray-500 shrink-0" title="Validation croisée (08/10/2026) : estimation honnête de l'erreur avant le réentraînement final sur 100% des données">
              Folds (CV)
            </label>
            <input
              type="number" min="1" max="20" value={kFolds}
              onChange={e => setKFolds(Math.max(1, Number(e.target.value)))}
              className="input w-16 text-center"
            />
            <button onClick={launch} className="btn btn-sm btn-primary flex-1">
              Lancer
            </button>
          </div>
        )}

        {feedback && <div className={`feedback-${feedback.type}`}>{feedback.msg}</div>}

        {logs.length > 0 && (
          <>
            <button
              onClick={() => setShowLogs(v => !v)}
              className="text-xs text-gray-400 hover:text-gray-600 self-start"
            >
              {showLogs ? "▲ Masquer les logs" : "▼ Afficher les logs"}
            </button>
            {showLogs && (
              <pre className="text-[10px] font-mono text-gray-600 bg-gray-50 border border-gray-100 rounded-lg p-2.5 max-h-44 overflow-y-auto leading-relaxed">
                {logs.join("\n")}
                <div ref={logsEndRef} />
              </pre>
            )}
          </>
        )}
      </div>
    </CollapsibleCard>
  );
}

/* ── Section : Rapport d'entraînement ──────────────────────────── */

function TrainingReport({ reportKey }) {
  const [open, setOpen]       = useState(false);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [sortKey, setSortKey] = useState("mean_error_s");
  const [sortAsc, setSortAsc] = useState(false);
  const prevKeyRef            = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setData(await apiFetch("/api/ai/training-report")); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open || data !== null) return;
    load();
  }, [open]);

  useEffect(() => {
    if (!open || !reportKey || reportKey === prevKeyRef.current) return;
    prevKeyRef.current = reportKey;
    load();
  }, [reportKey, open]);

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(v => !v);
    else { setSortKey(key); setSortAsc(false); }
  };

  const getVal = (sample, key) => {
    if (key === "t1") return sample.errors_s?.[0];
    if (key === "t2") return sample.errors_s?.[1];
    if (key === "t3") return sample.errors_s?.[2];
    if (key === "t4") return sample.errors_s?.[3];
    return sample[key];
  };

  const sorted = data?.samples
    ? [...data.samples].sort((a, b) => {
        const va = getVal(a, sortKey) ?? 0;
        const vb = getVal(b, sortKey) ?? 0;
        return sortAsc ? va - vb : vb - va;
      })
    : [];

  const SortTH = ({ k, label, left = false }) => (
    <th
      onClick={() => handleSort(k)}
      className={`pb-1 font-medium cursor-pointer hover:text-violet-600 select-none ${left ? "text-left" : "text-right"}`}
    >
      {label}{sortKey === k ? (sortAsc ? " ▲" : " ▼") : ""}
    </th>
  );

  return (
    <CollapsibleCard
      icon="📋"
      title="Rapport d'entraînement"
      badge={data ? `${data.samples?.length ?? 0} samples` : null}
      onOpenChange={setOpen}
    >
      <div className="p-4 flex flex-col gap-3">
        {loading && <div className="flex items-center gap-2 text-sm text-gray-400"><Spinner /> Chargement…</div>}
        {error && (
          <div className="feedback-error">
            {error} — <button onClick={load} className="underline">Réessayer</button>
          </div>
        )}
        {data && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-gray-700">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <SortTH k="form_id" label="Formulaire" left />
                  <SortTH k="duration_s" label="Durée" />
                  <SortTH k="t1" label="T1" />
                  <SortTH k="t2" label="T2" />
                  <SortTH k="t3" label="T3" />
                  <SortTH k="t4" label="T4" />
                  <SortTH k="mean_error_s" label="Moy." />
                </tr>
              </thead>
              <tbody>
                {sorted.map((s, i) => (
                  <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                    <td className="py-1.5 font-mono">
                      {s.site_id ? (
                        <button
                          type="button"
                          onClick={() => {
                            const origin = window.location.origin;
                            // Chemins relatifs : app.py les route via BACKEND_URL (réseau Docker
                            // interne), sans passer par nginx — voir Traitement.jsx pour le détail.
                            const rawUrl  = `/streamlit/export_measure_v3/${s.site_id}/${s.form_id}`;
                            const normUrl = `/streamlit/export_measure_normalized_v3/${s.site_id}/${s.form_id}`;
                            const vis = new URL("/visualisation/", origin);
                            vis.searchParams.set("csv_raw",  rawUrl);
                            vis.searchParams.set("csv_norm", normUrl);
                            vis.searchParams.set("form_id",  s.form_id);
                            vis.searchParams.set("site_id",  s.site_id);
                            window.open(vis.toString(), "_blank", "noopener,noreferrer");
                          }}
                          className="text-violet-600 hover:underline cursor-pointer"
                        >
                          {s.form_id}
                        </button>
                      ) : (
                        <span className="text-gray-400" title="site_id introuvable">{s.form_id}</span>
                      )}
                      {s.inverted && (
                        <span
                          title="Essai à phases inversées"
                          className="ml-1 inline-block text-[9px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 align-middle"
                        >
                          inv.
                        </span>
                      )}
                      {s.sens_inverse && (
                        <span
                          title="L'IA a prédit au moins une paire de marqueurs dans le mauvais sens (indépendamment de l'écart en secondes)"
                          className="ml-1 inline-block text-[9px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-1 align-middle"
                        >
                          sens ↩
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-right text-gray-500">
                      {s.duration_s ? `${Math.round(s.duration_s)}s` : "—"}
                    </td>
                    <td className={`py-1.5 text-right ${errClass(s.errors_s?.[0])}`}>{fmtErr(s.errors_s?.[0])}</td>
                    <td className={`py-1.5 text-right ${errClass(s.errors_s?.[1])}`}>{fmtErr(s.errors_s?.[1])}</td>
                    <td className={`py-1.5 text-right ${errClass(s.errors_s?.[2])}`}>{fmtErr(s.errors_s?.[2])}</td>
                    <td className={`py-1.5 text-right ${errClass(s.errors_s?.[3])}`}>{fmtErr(s.errors_s?.[3])}</td>
                    <td className={`py-1.5 text-right ${errClass(s.mean_error_s)}`}>{fmtErr(s.mean_error_s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
}

/* ── Section : Grid Search ──────────────────────────────────────── */

function GridSearch() {
  const [open, setOpen]           = useState(false);
  const [results, setResults]     = useState(null);
  const [gsStatus, setGsStatus]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [strategy, setStrategy]   = useState("greedy");
  const [maxCombos, setMaxCombos] = useState(50);
  const [gsEpochs, setGsEpochs]   = useState(15);
  const [feedback, showFeedback]  = useFeedback();
  const pollingRef                = useRef(null);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setResults(await apiFetch("/api/ai/grid-search-report")); }
    catch (e) { if (!e.message?.includes("404")) setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const fetchGsStatus = useCallback(async () => {
    const s = await apiFetch("/api/ai/grid-search-status").catch(() => null);
    setGsStatus(s);
    return s;
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  }, []);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      const s = await fetchGsStatus();
      if (s?.status !== "running") { stopPolling(); fetchResults(); }
    }, 3000);
  }, [fetchGsStatus, stopPolling, fetchResults]);

  useEffect(() => {
    if (!open) return;
    if (results === null) fetchResults();
    fetchGsStatus().then(s => { if (s?.status === "running") startPolling(); });
  }, [open]);

  useEffect(() => stopPolling, []);

  const launch = async () => {
    try {
      const combosParam = strategy === "random" ? `&max_combos=${maxCombos}` : "";
      const data = await apiFetch(
        `/api/ai/grid-search?strategy=${strategy}&epochs=${gsEpochs}${combosParam}`,
        { method: "POST" }
      );
      if (data.status === "already_running") {
        showFeedback("error", "Un grid search est déjà en cours.");
        return;
      }
      showFeedback("success", data.message);
      fetchGsStatus();
      startPolling();
    } catch (e) {
      showFeedback("error", e.message);
    }
  };

  const isRunning = gsStatus?.status === "running";
  const progress  = isRunning && gsStatus.total_combos > 0
    ? Math.round((gsStatus.current_combo / gsStatus.total_combos) * 100)
    : null;

  const best = results?.best_per_channel;

  return (
    <CollapsibleCard
      icon="🔬"
      title="Grid Search"
      badge={best ? "résultats disponibles" : null}
      onOpenChange={setOpen}
    >
      <div className="p-4 flex flex-col gap-4">
        {loading && <div className="flex items-center gap-2 text-sm text-gray-400"><Spinner /> Chargement…</div>}
        {error && <div className="feedback-error">{error}</div>}

        {best && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-600">Meilleures features par canal</p>
              <a
                href="/api/ai/grid-search-report.html"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-violet-600 hover:underline"
              >
                Rapport complet ↗
              </a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {["static", "transition", "dynamic", "orientation"].map(ch => {
                const ch_data = best[ch];
                if (!ch_data) return null;
                const isClassification = ch_data.accuracy != null;
                return (
                  <div key={ch} className="rounded-lg border border-gray-100 bg-gray-50 p-3 flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700">{CHANNEL_LABELS[ch]}</span>
                      <span className={isClassification ? "text-xs text-gray-700" : `text-xs ${errClass(ch_data.channel_error)}`}>
                        {isClassification ? `${Math.round(ch_data.accuracy * 100)}%` : fmtErr(ch_data.channel_error)}
                      </span>
                    </div>
                    {ch_data.orientation_mode && (
                      <span className="text-[10px] text-gray-400">mode : {ch_data.orientation_mode}</span>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {(ch_data.features || []).map(f => (
                        <span key={f} className="text-[10px] font-mono bg-white border border-gray-200 rounded px-1 py-0.5 text-gray-600">
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="divider" />

        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-gray-600">Lancer un grid search</p>

          {isRunning ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span className="flex items-center gap-1.5"><Spinner /> En cours…</span>
                <span>{gsStatus.current_combo} / {gsStatus.total_combos} combinaisons</span>
              </div>
              {progress != null && (
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="bg-violet-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-xs text-gray-500 shrink-0">Mode</label>
                <select
                  value={strategy}
                  onChange={e => setStrategy(e.target.value)}
                  className="input w-auto text-center"
                >
                  <option value="greedy">Glouton (recommandé)</option>
                  <option value="random">Aléatoire (ancien mode)</option>
                </select>
                {strategy === "random" && (
                  <>
                    <label className="text-xs text-gray-500 shrink-0">Combinaisons</label>
                    <input
                      type="number" min="1" max="16383" value={maxCombos}
                      onChange={e => setMaxCombos(Math.max(1, Number(e.target.value)))}
                      className="input w-20 text-center"
                    />
                  </>
                )}
                <label className="text-xs text-gray-500 shrink-0">Epochs</label>
                <input
                  type="number" min="1" max="100" value={gsEpochs}
                  onChange={e => setGsEpochs(Math.max(1, Number(e.target.value)))}
                  className="input w-16 text-center"
                />
                <button onClick={launch} className="btn btn-sm btn-primary">
                  Lancer
                </button>
              </div>
              {strategy === "greedy" && (
                <p className="text-[11px] text-gray-400">
                  Teste groupe de features par groupe, s'arrête dès que plus rien n'améliore —
                  pas de nombre de combinaisons à régler.
                </p>
              )}
            </div>
          )}

          {feedback && <div className={`feedback-${feedback.type}`}>{feedback.msg}</div>}
        </div>
      </div>
    </CollapsibleCard>
  );
}

/* ── Page ───────────────────────────────────────────────────────── */

export default function EntrainementIA() {
  const [reportKey, setReportKey] = useState(null);

  // 08/10/2026 (demande Jordan) : un entraînement terminé rafraîchit directement le "Statut du
  // modèle" (cartes d'erreur moyenne) et le "Rapport d'entraînement" (tableau par échantillon),
  // plutôt que de laisser ces modules affichés tant qu'on ne clique pas sur "Générer le rapport"
  // — le fichier training_report.json, lui, est déjà régénéré automatiquement par
  // ai/train_hybrid.py à chaque run, seul l'affichage ne suivait pas.
  const refreshReport = useCallback(() => setReportKey(Date.now()), []);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
        <div className="md:col-span-3">
          <ModelStatus onReportGenerated={setReportKey} refreshSignal={reportKey} />
        </div>
        <div className="md:col-span-1">
          <TrainingLauncher onTrainingComplete={refreshReport} />
        </div>
      </div>
      <TrainingReport reportKey={reportKey} />
      <GridSearch />
    </div>
  );
}
